import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SocialUser } from "@/types/social-pay.types";

import { PulsarService } from "@/services/pulsar.service";

import { useAuth } from "./useAuth";
// Analytics tracking removed - replace with your preferred analytics solution
import { toast } from "sonner";
import { getErrorMessage } from "@/helpers/getErrorMessage";
import { CreateTelegramUser } from "@/components/socials/telegram-login-button";

export const useGetSocialUser = () => {
  const { isAuthenticated, primaryWallet } = useAuth();

  return useQuery<SocialUser>({
    queryKey: ["social-pay-user", primaryWallet?.address],
    queryFn: async () => {
      const user = (await PulsarService.getSocialUser()).data;
      return user;
    },
    staleTime: 1000 * 30,
    enabled: isAuthenticated,
  });
};

export const useSocialPayMutations = () => {
  // const plausible = usePlausible(); // TODO: Replace with Vite-compatible analytics
  const queryClient = useQueryClient();

  const linkTwitter = useMutation({
    mutationFn: async (body: { redirectUrl: string }) => {
      const resp = await PulsarService.registerTwitter(body.redirectUrl);
      const url = resp.data;
      if (!url) {
        throw new Error("No OAuth URL present");
      }
      // Track event: "Social Pay: Link X"

      window.location.href = url;
    },
    onError: (e) => {
      toast.error(getErrorMessage(e, "Failed to link Twitter account"));
    },
  });

  const linkTelegram = useMutation({
    mutationFn: async (user: CreateTelegramUser) => {
      const resp = await PulsarService.registerTelegram(user);
      const url = resp.data;
      if (!url) {
        throw new Error("No OAuth URL present");
      }
      // Track event: "Social Pay: Link Telegram"

      queryClient.invalidateQueries({
        queryKey: ["social-pay-user"],
      });
    },
  });

  const unlinkTelegram = useMutation({
    mutationFn: async () => {
      const resp = await PulsarService.unlinkTelegram();
      // @ts-ignore
      toast.promise(resp, {
        loading: "Unlinking Telegram account...",
        success: "Telegram account unlinked",
        error: "Failed to unlink Telegram account",
      });
      await resp;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["social-pay-user"],
      });
    },
  });

  const unlinkTwitter = useMutation({
    mutationFn: async () => {
      const resp = await PulsarService.unlinkTwitter();
      // @ts-ignore
      toast.promise(resp, {
        loading: "Unlinking Twitter account...",
        success: "Twitter account unlinked",
        error: "Failed to unlink Twitter account",
      });
      // Track event: "Social Pay: Unlink X"
      await resp;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["social-pay-user"],
      });
    },
  });

  const unlinkDiscord = useMutation({
    mutationFn: async () => {
      const resp = await PulsarService.unregisterDiscord();
      // @ts-ignore
      toast.promise(resp, {
        loading: "Unlinking Discord account...",
        success: "Discord account unlinked",
        error: "Failed to unlink Discord account",
      });
      await resp;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["social-pay-user"],
      });
    },
  });

  const linkDiscord = useMutation({
    mutationFn: async () => {
      const redirectUrl = window.location.href;
      const resp = await PulsarService.registerDiscord(redirectUrl);
      const url = resp.data;
      if (!url) {
        throw new Error("No OAuth URL present");
      }
      // Track event: "Social Pay: Link Discord"

      window.location.href = url;
    },
    onError: (e) => {
      toast.error(getErrorMessage(e, "Failed to link Discord account"));
    },
  });

  return {
    linkTwitter,
    unlinkTwitter,
    linkTelegram,
    unlinkTelegram,
    linkDiscord,
    unlinkDiscord,
  };
};
